import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import classNames from 'classnames'
import { canSkipOnRowSelected } from 'helpers'
import TableUsers from 'components/ui/TableUsers'
import listActiveParticipants, {
  UsersSearchParams,
} from 'api/administration/participants/ListActiveParticipants'
import { UserAttributes } from 'api/administration/participants/types'
import { useUsersColumns } from '../../../common/hooks'
import { LoadingDataProgress } from '../../../common/LoadingDataProgress'
import styles from './SelectUsers.module.scss'

interface SelectUsersProps {
  selectedMap: Map<number, UserAttributes>
  usersAdded: UserAttributes[]
  onAdd: (users: UserAttributes[]) => void
  onRemove?: (users: UserAttributes[]) => void
  filterData?: IFilterData
  onStartLoading?: () => void
  onStopLoading?: () => void
  filter?(users: UserAttributes[]): UserAttributes[]
}

export const SelectUsers: React.FC<SelectUsersProps> = ({
  selectedMap,
  usersAdded,
  onAdd,
  onRemove,
  filterData,
  onStartLoading,
  onStopLoading,
}) => {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({})
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [fastFilters, setFastFilters] = useState(0 as any)

  const [loading, setLoading] = useState(false)
  const [loadingAll, setLoadingAll] = useState(false)
  const [list, setList] = useState<UserAttributes[]>([])
  const [total, setTotal] = useState(0)

  const params = useMemo(
    (): Omit<UsersSearchParams, 'page' | 'perPage'> => ({
      searchText: search,
      sort,
      usersFilter: filterData,
      fastFilters,
    }),
    [search, filterData, sort, fastFilters]
  )

  const selectedUsers = useMemo(
    () => Array.from(selectedMap.values()),
    [selectedMap]
  )

  const onAddUsersRef = useRef(onAdd)
  useEffect(() => {
    onAddUsersRef.current = onAdd
  }, [onAdd])

  useEffect(() => {
    setLoading(true)
    listActiveParticipants({
      page,
      perPage,
      ...params,
    })
      .then(({ data, total }) => {
        setList(data)
        setTotal(total)
      })
      .finally(() => setLoading(false))
  }, [page, params, perPage])

  const dataSource = useMemo(
    () =>
      list.map((user) => ({
        key: user.id,
        ...user,
      })),
    [list]
  )

  const columns = useUsersColumns()

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const [usersLoaded, setUsersLoaded] = useState<UserAttributes[]>([])
  const setUsersLoadedRef = useRef(setUsersLoaded)
  useEffect(() => {
    setUsersLoadedRef.current = setUsersLoaded
  }, [setUsersLoaded])

  const handleAllSelected = useCallback(async () => {
    const limit = 150
    let fetchData = true
    let page = 1
    let users: UserAttributes[] = []

    setLoadingAll(true)
    onStartLoading?.()
    try {
      while (fetchData) {
        if (!onAdd) return
        const { data } = await listActiveParticipants({
          page,
          perPage: limit,
          ...params,
        })
        if (data.length === 0 || data.length < limit) fetchData = false
        users = [...users, ...data]
        setUsersLoaded(users)
        page++
      }
    } catch (e) {
      console.error(e)
    }
    setLoadingAll(false)
    onAdd(users)
    onStopLoading?.()
    setUsersLoaded([])
  }, [onAdd, onStartLoading, onStopLoading, params])

  const handleAllSelectedRef = useRef(handleAllSelected)

  useEffect(() => {
    handleAllSelectedRef.current = handleAllSelected
  }, [handleAllSelected])

  const handleSetFastAction = (value: any) => {
    setFastFilters(value)
    setPage(1)
  }

  return (
    <div className={classNames('position-relative', styles.container)}>
      <TableUsers
        changeColumnsVisibility
        responsive
        delaySearch
        data={dataSource.filter(
          (user) => !usersAdded.map((item) => item.id).includes(user.id)
        )}
        search={search}
        onSearch={handleSearch}
        total={total - usersAdded.length}
        columnHeaders={columns}
        onSortChange={(sortData: any) => setSort(sortData)}
        onRowSelected={({ selectedRows, selectedRowKeys, dispatcher }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          if (dispatcher === 'handleUnselectAllRows') {
            onRemove?.(selectedUsers)
            return
          }
          const newUsers = selectedRows.filter(
            ({ id }) => !selectedMap.has(id)
          ) as UserAttributes[]

          const keysSet = new Set(selectedRowKeys)

          const deletedUsers = selectedUsers.filter(
            ({ id }) => !keysSet.has(id)
          )

          // Rankmi-table has a bug, use onAllRowKeys how reference
          if (newUsers.length) onAddUsersRef.current(selectedRows)

          if (deletedUsers.length) onRemove?.(deletedUsers)
        }}
        selectedRows={selectedUsers.map(({ id }) => id)}
        loading={loading || loadingAll}
        onPaginationChange={(currentPage: number) => setPage(currentPage)}
        simpleSelectAll={true}
        onRowsPerPageChange={(itemsPerPage: number) => setPerPage(itemsPerPage)}
        onAllRowKeys={() => {
          // Rankmi-table has a bug, use onAllRowKeys how reference
          handleAllSelectedRef.current()
        }}
        onFastActionSelect={handleSetFastAction}
        showScrollButtons
      />
      {loadingAll && (
        <LoadingDataProgress loaded={usersLoaded.length} total={total} />
      )}
    </div>
  )
}

export default SelectUsers
